import { Component, OnInit, Inject } from '@angular/core';
import {
    PageComponent,
    _,
    SharedFacade,
    ICC_PAGE_DATA,
    ModalService,
    ConfigurationsService,
} from '@icc/configurator/shared';
import { iccListItem } from '@icc/configurator/ui';
import { IccAccessoryAccessory } from '@icc/construction-elements/types';
import { ConstructColorDto as IccConstructColor } from '@icc/construction-elements/types';
import { AccessoriesOptionsPageComponent } from '../accessories-options-page/accessories-options-page.component';
import { ActiveSash } from '@icc/window-door/types';
import {
    AppConfigFactory,
    APP_CONFIG,
    ConfiguratorsDataService,
    TranslateService,
} from '@icc/shared';

import { AccessoriesListPageStoreService } from './accessories-list-page-store.service';
import { Categories, Category } from './categories.interface';
@Component({
    selector: 'icc-accessories-list-page',
    templateUrl: './accessories-list-page.component.html',
    styleUrls: ['./accessories-list-page.component.scss'],
    standalone: false
})
export class AccessoriesListPageComponent extends PageComponent implements OnInit {
    public title = this.translateService.instant('ACCESSORY|Wybierz akcesoria dla');
    public options = [];

    accessories: iccListItem[] = [];
    arrayOfDependencies = [];
    accessoryId!: number;
    frameSide;

    constructor(
        protected sharedFacade: SharedFacade,
        protected modalService: ModalService,
        protected translateService: TranslateService,
        protected configurationsService: ConfigurationsService<'door'>,
        protected configuratorsDataService: ConfiguratorsDataService,
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        protected accessoriesListPageStoreService: AccessoriesListPageStoreService,
        @Inject(ICC_PAGE_DATA)
        protected pageData: {
            accessories: IccAccessoryAccessory[];
            categories: any[];
            subcategories: any[];
            forIt: 'configuration' | 'sash' | 'side' | 'drive';
            sashNum?: number;
            sash?: ActiveSash | string;
            colors?: IccConstructColor[];
            noPrice: boolean;
        }
    ) {
        super();
        let subtitle = '';
        switch (pageData.forIt) {
            case 'configuration':
                subtitle = this.translateService.instant('ACCESSORY|całej konstrukcji');
                break;
            case 'drive':
                subtitle = this.translateService.instant('ACCESSORY|sterowania rolety');
                break;
        }

        this.title =
            this.translateService.instant('ACCESSORY|Wybierz akcesoria dla') + ' ' + subtitle;
    }

    ngOnInit() {
        if (
            this.config().filterAccessoriesByCategories &&
            this.pageData.forIt === 'configuration'
        ) {
            this.getAccessoriesBasedOnCategories();
        } else {
            this.getAccessories();
        }

        const selectedAccessories = this.configurationsService.conf.Current.Accessories;

        if (selectedAccessories?.length) {
            this.disableAccessories(selectedAccessories);
        }
    }

    protected getAccessoriesBasedOnCategories() {
        this.accessoriesListPageStoreService.selectedCategoryObs$.subscribe((categories) => {
            if (!(categories?.category?.id || categories?.subcategory?.id)) {
                return;
            }
            this.setAccessoriesBasedOnCategories(categories);
        });
    }

    protected getAccessories() {
        this.accessories = this.pageData.accessories.map<iccListItem>((accessory) => ({
            id: Number(accessory.id),
            categoryId: Number(accessory.window_accessories_category_id),
            title: accessory.name,
            imageUrl: `/files/windowaccessory/${accessory.img}`,
            description: `${accessory.description}`,
        }));
    }

    protected setAccessoriesBasedOnCategories(categories) {
        const subCategories = this.getSubCategories(categories);
        const mainCategories = this.getMainCategories(categories);

        if (subCategories?.length && categories.subcategory.id !== Category.allSubCategories) {
            this.accessories = [...subCategories];
        } else if(categories.subcategory.id === Category.allSubCategories && categories.category.id !== Category.allCategories) {
            this.accessories = [...mainCategories];
        } else {
            this.accessories = [...mainCategories, ...subCategories];
        }
    }

    protected getSubCategories(categories: Categories) {
        if (!categories) {
            return;
        }

        if (categories.subcategory.id === Category.allSubCategories) {
            const subcategoriesFromParentCategory = categories.category.id === Category.allCategories
                ? this.configuratorsDataService.data.windowAccessoriesCategories.filter(category => category.parent_id !== null).map(k => k.id)
                : this.getSubcategoriesFromParentCategoryBasedOnId(Number(categories.category.id));
            const accessories = this.pageData.accessories
                .filter(p => subcategoriesFromParentCategory.find(catId => Number(catId) === Number(p.window_accessories_category_id)));
            return this.getProjectedIccListItem(accessories);
        } else {
            const accessories = this.pageData.accessories.filter(
                (p) =>
                    Number(p.window_accessories_category_id) === Number(categories.subcategory.id)
            );
            return this.getProjectedIccListItem(accessories);
        }
    }

    protected getMainCategories(categories: Categories) {
        if (!categories) {
            return;
        }

        if (categories.category.id === Category.allCategories) {
            const accessories = this.pageData.accessories.filter((p) =>
                this.pageData.categories.some(
                    (sub) => Number(sub.id) === Number(p.window_accessories_category_id)
                )
            );
            return this.getProjectedIccListItem(accessories);
        } else {
            const parentCategories = this.configuratorsDataService.data.windowAccessoriesCategories
                .filter((category) => Number(category.parent_id) === Number(categories.category.id))
                .map((k) => k.id);

            const categoryIds = parentCategories.length
                ? this.getSubcategoriesFromParentCategoryBasedOnId(Number(categories.category.id))
                : [];

            const accessories = this.pageData.accessories.filter(
                (p) =>
                    Number(p.window_accessories_category_id) === Number(categories.category.id) ||
                    (categoryIds.length &&
                        categoryIds.find(
                            (cId) => Number(cId) === Number(p.window_accessories_category_id)
                        ))
            );
            return this.getProjectedIccListItem(accessories);
        }
    }

    protected getProjectedIccListItem(listItem: any[]): any[] {
        if (!listItem.length) return [];

        return listItem.map<iccListItem>((accessory) => ({
            id: Number(accessory.id),
            categoryId: Number(accessory.window_accessories_category_id),
            title: accessory.name,
            imageUrl: `/files/windowaccessory/${accessory.img}`,
            description: `${accessory.description}`,
        }));
    }

    getSubcategoriesFromParentCategoryBasedOnId(id: number) {
        const parentCategories = this.configuratorsDataService.data.windowAccessoriesCategories.filter(
            (c: any) => c.parent_id
        );

        if (!parentCategories.length) {
            return [];
        }

        return this.accessoriesListPageStoreService.getCategories(id, parentCategories);
    }

    selectAccessory(item: iccListItem) {
        const accessory = this.pageData.accessories.find((a) => Number(a.id) === Number(item.id));
        if (accessory) {
            this.modalService
                .open({
                    pageComponent: AccessoriesOptionsPageComponent,
                    resolve: {
                        accessory,
                        forIt: this.pageData.forIt,
                        sash: this.pageData.sash,
                        colors: this.pageData.colors,
                    },
                })
                .result.then((result) => {
                    if (result) {
                        this.sharedFacade.closePage(result);
                    }
                });
        }
    }

    disableAccessories(selectedAccessories) {
        const accCategories =
        this.configuratorsDataService.data.windowAccessoriesCategories || [];
        const selectedCategoriesOnlyOne = [];
        const selectedCategoriesOnlySingle = [];

        selectedAccessories.forEach((acc) => {
            const category = accCategories.find((cat) => cat.id === acc.accessory.window_accessories_category_id);
            if (category.only_one_accessory) {
                selectedCategoriesOnlyOne.push(category);
            }
            if (category.only_single_accessory) {
                selectedCategoriesOnlySingle.push(category);
            }
        });

        this.accessories.forEach(acc => {
            if (selectedCategoriesOnlyOne.some(cat => Number(cat.id) === acc.categoryId)) {
                acc.disabled = true;
            }
            if (
                selectedCategoriesOnlySingle.some(cat => Number(cat.id) === acc.categoryId)
                && selectedAccessories.some(selAcc => Number(selAcc.accessory.id) === acc.id)
                ) {
                    acc.disabled = true;
                }
        })
    }

    disableAccessoriesPerSash(selectedAccessoriesInSash) {
        const accCategories =
            this.configuratorsDataService.data.windowAccessoriesCategories || [];
        const selectedCategoriesOnlyOnePerSash = [];
        const selectedCategoriesOnlySinglePerSash = [];

        selectedAccessoriesInSash.forEach((acc) => {
            const category = accCategories.find(
                (cat) => cat.id === acc.accessory.window_accessories_category_id
            );
            if (category?.only_one_accessory_per_sash) {
                selectedCategoriesOnlyOnePerSash.push(category);
            }
            if (category?.only_single_accessory_per_sash) {
                selectedCategoriesOnlySinglePerSash.push(category);
            }
        });

        this.accessories.forEach(acc => {
            if (selectedCategoriesOnlyOnePerSash.some(cat => Number(cat.id) === acc.categoryId)) {
                acc.disabled = true;
            }
            if (
                selectedCategoriesOnlySinglePerSash.some(cat => Number(cat.id) === acc.categoryId)
                && selectedAccessoriesInSash.some(selAcc => Number(selAcc.accessory.id) === acc.id)
            ) {
                acc.disabled = true;
            }
        });
    }
}
